import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faBook, faBullseye, faChartLine, faCog, faExternalLink, faFolder, faGamepad, faLanguage, faPlus, faSearch, faStar, faArrowUpRight, faPlay } from '@fortawesome/pro-solid-svg-icons';
import Button from '../../components/general/Button';

const GetStartedPage = () => {
    const navigate = useNavigate();
    const dashboardFeatures = [
        {
            icon: faFolder,
            title: "Create & Organize Decks",
            description: "Build your vocabulary decks and organize them by language, topic, or difficulty level.",
            link: "/my-library",
            linkText: "Go to My Decks"
        },
        {
            icon: faPlus,
            title: "Add Vocabulary Cards",
            description: "Create flashcards with target words, translations, and examples to build your personal learning library.",
            link: "/my-library",
            linkText: "Create Deck"
        },
        {
            icon: faBullseye,
            title: "Set Daily Goals",
            description: "Define your learning targets and track your progress with customizable daily goals.",
            link: "/my-goals/daily",
            linkText: "Set Goals"
        },
        {
            icon: faChartLine,
            title: "Track Progress",
            description: "Monitor your learning statistics, streaks, and achievements across all your languages.",
            link: "/dashboard",
            linkText: "View Analytics"
        },
        {
            icon: faCog,
            title: "Customize Settings",
            description: "Personalize your learning experience with language preferences and study settings.",
            link: "/settings",
            linkText: "Open Settings"
        },
        {
            icon: faSearch,
            title: "Import Content",
            description: "Import your existing content from other sources to Yalango.",
            link: "/import",
            linkText: "Import Content"
        },
        {
            icon: faLanguage,
            title: "Manage Languages",
            description: "Administer the languages you are learning and studying. Add, remove, or update your language preferences.",
            link: "/my-languages",
            linkText: "Manage Languages"
        }
    ];

    const studySiteFeatures = [
        {
            icon: faGamepad,
            title: "Interactive Games",
            description: "Practice with engaging games like flashcards and quizzes."
        },
        {
            icon: faBook,
            title: "Study Your Vocabulary",
            description: "We keep track of your progress and vocabulary on Yalango, and give you exercises based on spaced repetition."
        },
        {
            icon: faStar,
            title: "Discover New Content",
            description: "Explore community-created decks and discover new learning materials."
        },
        {
            icon: faBullseye,
            title: "Achieve Your Goals",
            description: "Complete your daily learning targets and build consistent study habits."
        }
    ];

    const gettingStartedSteps = [
        {
            step: "1",
            title: "Choose Your Language",
            description: "Select the language you want to learn from the language menu in the sidebar."
        },
        {
            step: "2", 
            title: "Create Your First Deck",
            description: "Go to 'My Decks' and create a new vocabulary deck for your chosen language."
        },
        {
            step: "3",
            title: "Add Vocabulary Cards",
            description: "Start adding words and phrases to your deck with translations and examples."
        },
        {
            step: "4",
            title: "Set Your Goals",
            description: "Define how many words you want to learn each day in the Goals section."
        },
        {
            step: "5",
            title: "Start Studying",
            description: "Visit yalango.com to practice with your decks and track your progress."
        }
    ];

    return (
        <div className="max-w-6xl mx-auto px-6 py-12">
            {/* Header Section */}
            <div className="text-center mb-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <h1 className="text-4xl font-bold bg-gradient-to-r p-2 from-persian-green to-darker-persian-green bg-clip-text text-transparent mb-4">
                        Get Started with Yalango
                    </h1>
                    <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                        Learn how to use Yalango and discover all the features available to enhance your language learning journey.
                    </p>
                </motion.div>
            </div>

            {/* Two Sites Explanation */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="mb-12"
            >
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
                    <div className="bg-gradient-to-r from-persian-green to-darker-persian-green px-8 py-6">
                        <h2 className="text-2xl font-semibold text-white flex items-center gap-3">
                            <FontAwesomeIcon icon={faLanguage} className="text-white" />
                            Two Sites, One Learning Experience
                        </h2>
                        <p className="text-white text-opacity-90 mt-1">Understanding the Yalango ecosystem</p>
                    </div>
                    
                    <div className="p-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="group">
                                <div className="p-6 bg-gray-50 dark:bg-gray-700 rounded-xl">
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-persian-green to-darker-persian-green rounded-xl flex items-center justify-center">
                                            <FontAwesomeIcon icon={faCog} className="text-white text-xl" />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
                                                dashboard.yalango.com
                                            </h3>
                                            <p className="text-gray-600 dark:text-gray-400">Your content management hub</p>
                                        </div>
                                    </div>
                                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                                        Organize, create, and edit your content. View detailed statistics and manage your learning materials.
                                    </p>
                                </div>
                            </div>

                            <div className="group">
                                <div className="p-6 bg-gray-50 dark:bg-gray-700 rounded-xl">
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                                            <FontAwesomeIcon icon={faBook} className="text-white text-xl" />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
                                                yalango.com
                                            </h3>
                                            <p className="text-gray-600 dark:text-gray-400">Your study and practice platform</p>
                                        </div>
                                    </div>
                                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                                        Study and review your materials. Discover new content and practice with interactive exercises.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Dashboard Features Section */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mb-12"
            >
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 px-8 py-6 border-b border-gray-200 dark:border-gray-600">
                        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white flex items-center gap-3">
                            <FontAwesomeIcon icon={faCog} className="text-gray-600 dark:text-gray-400" />
                            Dashboard Features
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mt-1">Your command center for creating and managing content</p>
                    </div>
                    
                    <div className="p-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {dashboardFeatures.map((feature, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 + (index * 0.1) }}
                                    className="group"
                                >
                                    <div className="p-6 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                                        <div className="flex items-center gap-4 mb-4">
                                            <div className="w-10 h-10 bg-gradient-to-br from-persian-green to-darker-persian-green rounded-lg flex items-center justify-center">
                                                <FontAwesomeIcon 
                                                    icon={feature.icon} 
                                                    className="text-white text-sm" 
                                                />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-gray-800 dark:text-white">
                                                    {feature.title}
                                                </h3>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    {feature.description}
                                                </p>
                                            </div>
                                        </div>
                                        <motion.button
                                            onClick={() => navigate(feature.link)}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            className="inline-flex items-center text-persian-green hover:text-darker-persian-green font-medium text-sm transition-colors duration-200"
                                        >
                                            {feature.linkText}
                                            <FontAwesomeIcon icon={faArrowUpRight} className="ml-1 text-xs" />
                                        </motion.button>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Study Site Explanation */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mb-12"
            >
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-8 py-6">
                        <h2 className="text-2xl font-semibold text-white flex items-center gap-3">
                            <FontAwesomeIcon icon={faBook} className="text-white" />
                            Study at yalango.com
                        </h2>
                        <p className="text-white text-opacity-90 mt-1">Practice, play games, and achieve your learning goals</p>
                    </div>
                    
                    <div className="p-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            {studySiteFeatures.map((feature, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.5 + (index * 0.1) }}
                                    className="group"
                                >
                                    <div className="p-6 bg-gray-50 dark:bg-gray-700 rounded-xl">
                                        <div className="flex items-center gap-4 mb-3">
                                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                                                <FontAwesomeIcon 
                                                    icon={feature.icon} 
                                                    className="text-white text-sm" 
                                                />
                                            </div>
                                            <h3 className="font-semibold text-gray-800 dark:text-white">
                                                {feature.title}
                                            </h3>
                                        </div>
                                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                                            {feature.description}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        <div className="text-center">
                            <motion.a
                                href="https://yalango.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="inline-flex no-underline items-center bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium py-3 px-8 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200"
                            >
                                Visit yalango.com
                                <FontAwesomeIcon icon={faExternalLink} className="ml-2" />
                            </motion.a>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Getting Started Steps */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="mb-12"
            >
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 px-8 py-6 border-b border-gray-200 dark:border-gray-600">
                        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white flex items-center gap-3">
                            <FontAwesomeIcon icon={faPlay} className="text-gray-600 dark:text-gray-400" />
                            Getting Started
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mt-1">Follow these simple steps to begin your language learning journey</p>
                    </div>
                    
                    <div className="p-8">
                        <div className="space-y-6">
                            {gettingStartedSteps.map((step, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.7 + (index * 0.1) }}
                                    className="group"
                                >
                                    <div className="flex items-start p-6 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                                        <div className="bg-gradient-to-br from-persian-green to-darker-persian-green text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-sm mr-6 flex-shrink-0 shadow-lg">
                                            {step.step}
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-semibold text-gray-800 dark:text-white mb-2">
                                                {step.title}
                                            </h3>
                                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                                {step.description}
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Call to Action */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="text-center"
            >
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
                    <div className="bg-gradient-to-r from-persian-green to-darker-persian-green px-8 py-6">
                        <h3 className="text-2xl font-semibold text-white flex items-center justify-center gap-3">
                            <FontAwesomeIcon icon={faPlay} className="text-white" />
                            Ready to Start Learning?
                        </h3>
                        <p className="text-white text-opacity-90 mt-1">Begin your language learning journey today</p>
                    </div>
                    
                    <div className="p-8">
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                            Begin by selecting your target language and creating your first deck.
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-500 mb-8">
                            💡 You can easily find this get started page again from the settings page anytime you need a refresher.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button 
                                text="Go to My Library"
                                color="green"
                                size="normal"
                                onClick={() => navigate('/my-library')}
                            />
                            <Button 
                                text="Set Daily Goals"
                                color="charcoal"
                                size="normal"
                                onClick={() => navigate('/my-goals/daily')}
                            />
                            <Button 
                                text="View Analytics"
                                color="charcoal"
                                size="normal"
                                onClick={() => navigate('/dashboard')}
                            />
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default GetStartedPage;
